import { useState, useMemo, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { router } from '@inertiajs/react'
import { getDateLocale } from '@/lib/i18n'
import { csrfHeaders } from '@/lib/csrf'
import { useIsMobile } from '@/hooks/use-mobile'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { getDisplayUrl } from './LiveStatusBar'
import {
  ExternalLink,
  Eye,
  EyeOff,
  Copy,
  Star,
  X,
  ChevronDown,
  ChevronUp,
  RefreshCw,
  AlertTriangle,
  KeyRound,
} from 'lucide-react'
import { toast } from 'sonner'
import type { Project, ActiveWorkflow } from '@/types/conversations'
import { isWorkflowInProgress } from '@/lib/workflow-utils'
import { WorkflowProgressPanel } from '../messages/WorkflowProgressPanel'

interface LiveOverviewSectionProps {
  project: Project
  processing: boolean
  onRedeploy: () => void
  activeWorkflow?: ActiveWorkflow | null
  onAbortWorkflow?: () => void
  onRetryWorkflow?: () => Promise<void>
  isAbortingWorkflow?: boolean
  isRetryingWorkflow?: boolean
}

export function LiveOverviewSection({
  project,
  processing,
  onRedeploy,
  activeWorkflow,
  onAbortWorkflow,
  onRetryWorkflow,
  isAbortingWorkflow,
  isRetryingWorkflow,
}: LiveOverviewSectionProps) {
  const { t } = useTranslation('conversations')
  const isMobile = useIsMobile()
  const [showPassword, setShowPassword] = useState(false)
  const [credsExpanded, setCredsExpanded] = useState(false)
  const [isGeneratingCredentials, setIsGeneratingCredentials] = useState(false)
  const [isResettingCredentials, setIsResettingCredentials] = useState(false)

  const handleGenerateCredentials = useCallback(async () => {
    setIsGeneratingCredentials(true)
    try {
      const response = await fetch(`/projects/${project.id}/generate_production_credentials`, {
        method: 'POST',
        headers: csrfHeaders(),
      })
      if (response.ok) {
        router.reload({ only: ['project'] })
      } else {
        const data = await response.json()
        toast.error(t('live.credentials.generateFailed', { error: data.error }))
      }
    } finally {
      setIsGeneratingCredentials(false)
    }
  }, [project.id, t])

  const handleResetCredentials = useCallback(async () => {
    setIsResettingCredentials(true)
    try {
      const response = await fetch(`/projects/${project.id}/reset_production_credentials`, {
        method: 'POST',
        headers: csrfHeaders(),
      })
      if (response.ok) {
        router.reload({ only: ['project'] })
      } else {
        try {
          const data = await response.json()
          toast.error(t('live.credentials.resetFailed', { error: data.error }))
        } catch {
          toast.error(t('live.credentials.resetFailed', { error: response.statusText }))
        }
      }
    } finally {
      setIsResettingCredentials(false)
    }
  }, [project.id, t])

  const isSuspendedNoCredits = project.production_status === 'suspended_no_credits'
  const isFailed = project.production_status === 'deploy_failed' && !isWorkflowInProgress(activeWorkflow)

  const displayUrl = getDisplayUrl(project)

  const workflowMessage = useMemo(() => activeWorkflow ? {
    id: activeWorkflow.message_id ?? -1,
    workflow_step_data: {
      name: activeWorkflow.workflow_name,
      description: activeWorkflow.description,
      steps: activeWorkflow.steps_data,
      failure_mode: undefined,
      status: activeWorkflow.workflow_status,
      status_message: activeWorkflow.workflow_status_message,
      total_retry_count: activeWorkflow.total_retry_count,
    },
    status: activeWorkflow.status as 'pending' | 'processing' | 'completed' | 'failed' | 'interrupted',
    content: activeWorkflow.description,
    role: 'assistant' as const,
    message_type: 'info' as const,
    created_at: activeWorkflow.started_at ?? '',
  } : null, [activeWorkflow])

  const browserPreview = displayUrl && (
    <div className="border border-border rounded-lg overflow-hidden bg-background">
      {/* Browser chrome bar */}
      <div className="flex items-center gap-2 px-3 py-2 bg-muted/50 border-b border-border">
        <div className="flex items-center gap-1.5">
          <div className="w-2.5 h-2.5 rounded-full bg-red-500/70" />
          <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/70" />
          <div className="w-2.5 h-2.5 rounded-full bg-green-500/70" />
        </div>
        <div className="flex-1 flex items-center bg-background rounded px-2 py-0.5 text-[11px] font-mono text-muted-foreground truncate">
          {displayUrl.replace(/^https?:\/\//, '')}
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="h-5 w-5 flex-shrink-0"
          onClick={() => window.open(displayUrl, '_blank')}
        >
          <ExternalLink className="h-3 w-3" />
        </Button>
      </div>
      {/* Iframe with desktop aspect ratio */}
      <div className="relative aspect-[16/10]">
        <iframe
          src={displayUrl}
          className="absolute inset-0 w-full h-full"
          title="Production preview"
          sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-modals allow-downloads allow-popups-to-escape-sandbox"
          allow="microphone; camera; geolocation; clipboard-read; clipboard-write; fullscreen; autoplay; encrypted-media; payment; web-share"
        />
        <div className="absolute inset-0 cursor-default" />
      </div>
    </div>
  )

  const urlInfo = displayUrl && (
    <div className="space-y-1">
      <p className="text-xs text-muted-foreground">{t('live.production.url')}</p>
      <a
        href={displayUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="text-sm text-blue-600 dark:text-blue-400 hover:underline font-mono break-all inline-flex items-center gap-1.5"
      >
        {displayUrl.replace(/^https?:\/\//, '')}
        <ExternalLink className="h-3.5 w-3.5 flex-shrink-0" />
      </a>
      {project.production_deployed_at && (
        <p className="text-xs text-muted-foreground">
          {t('live.production.lastDeployed')}{' '}
          {new Date(project.production_deployed_at).toLocaleString(getDateLocale())}
        </p>
      )}
    </div>
  )

  const adminCredentials = project.production_user_password ? (
    <div className="border border-border rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setCredsExpanded(!credsExpanded)}
        className="w-full px-3 py-2.5 flex items-center justify-between hover:bg-muted/50 transition-colors"
      >
        <span className="text-sm font-medium">{t('live.sre.overview.adminCredentials')}</span>
        {credsExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
      </button>
      {credsExpanded && (
        <div className="border-t border-border px-3 py-3 space-y-2 bg-muted/30">
          <div>
            <p className="text-xs text-muted-foreground">{t('live.sre.overview.email')}</p>
            <div className="flex items-center gap-1.5">
              <code className="text-xs font-mono truncate">{project.production_user_email}</code>
              <Button
                variant="ghost"
                size="icon"
                className="h-5 w-5"
                onClick={() => {
                  navigator.clipboard.writeText(project.production_user_email!)
                  toast.success(t('chat.copied'))
                }}
              >
                <Copy className="h-3 w-3" />
              </Button>
            </div>
          </div>
          {project.production_user_password && (
            <div>
              <p className="text-xs text-muted-foreground">{t('live.sre.overview.password')}</p>
              <div className="flex items-center gap-1.5">
                <code className="text-xs font-mono">
                  {showPassword ? project.production_user_password : '••••••••'}
                </code>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-5 w-5"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-5 w-5"
                  onClick={() => {
                    navigator.clipboard.writeText(project.production_user_password!)
                    toast.success(t('chat.copied'))
                  }}
                >
                  <Copy className="h-3 w-3" />
                </Button>
              </div>
            </div>
          )}
          <Button
            onClick={handleResetCredentials}
            disabled={isResettingCredentials}
            size="sm"
            variant="outline"
            className="w-full"
          >
            {isResettingCredentials ? (
              <>
                <RefreshCw className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                {t('live.credentials.resetting')}
              </>
            ) : (
              <>
                <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
                {t('live.credentials.reset')}
              </>
            )}
          </Button>
        </div>
      )}
    </div>
  ) : project.production_status === 'deployed' ? (
    <div className="border border-border rounded-lg p-3 space-y-2">
      <div className="flex items-center gap-2">
        <KeyRound className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm font-medium">{t('live.credentials.title')}</span>
      </div>
      <p className="text-xs text-muted-foreground">{t('live.credentials.notGenerated')}</p>
      <Button
        onClick={handleGenerateCredentials}
        disabled={isGeneratingCredentials}
        size="sm"
        variant="outline"
      >
        {isGeneratingCredentials ? (
          <>
            <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
            {t('live.credentials.generating')}
          </>
        ) : (
          <>
            <KeyRound className="h-4 w-4 mr-2" />
            {t('live.credentials.generate')}
          </>
        )}
      </Button>
    </div>
  ) : null

  const deployHistory = (
    <DeployHistory
      project={project}
      processing={processing}
      onRedeploy={onRedeploy}
    />
  )

  return (
    <div className="p-4 space-y-4">
      {/* Active workflow progress — separate pane, appears/disappears */}
      {workflowMessage && (
        <WorkflowProgressPanel
          message={workflowMessage as any}
          onAbort={onAbortWorkflow}
          onRetry={onRetryWorkflow}
          isAborting={isAbortingWorkflow}
          isRetrying={isRetryingWorkflow}
        />
      )}

      {/* Status banners */}
      {!project.all_integrations_have_live_vars && project.production_status !== 'deploying' && (
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
          <p className="text-sm font-medium text-amber-900 dark:text-amber-300">
            {t('live.warnings.credentialsTitle')}
          </p>
          <p className="text-xs text-amber-700 dark:text-amber-400 mt-0.5">
            {t('live.warnings.credentialsDescription')}
          </p>
        </div>
      )}
      {project.live_env_vars_stale && project.production_status === 'deployed' && (
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
          <div className="flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium text-amber-900 dark:text-amber-300">
                {t('live.warnings.staleEnvVarsTitle')}
              </p>
              <p className="text-xs text-amber-700 dark:text-amber-400 mt-0.5">
                {t('live.warnings.staleEnvVarsDescription')}
              </p>
              <Button
                size="sm"
                variant="outline"
                className="mt-2 border-amber-300 dark:border-amber-700 text-amber-800 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-900/40"
                onClick={onRedeploy}
                disabled={processing}
              >
                <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
                {t('live.buttons.redeploy')}
              </Button>
            </div>
          </div>
        </div>
      )}
      {project.production_status === 'deploying' && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
          <div className="flex items-center gap-2">
            <RefreshCw className="h-4 w-4 text-blue-600 dark:text-blue-400 animate-spin" />
            <div>
              <p className="text-sm font-medium text-blue-900 dark:text-blue-300">
                {t('live.deployment.inProgress')}
              </p>
              <p className="text-xs text-blue-700 dark:text-blue-400 mt-0.5">
                {project.production_url
                  ? t('live.deployment.inProgressUpdate')
                  : t('live.deployment.inProgressNew')}
              </p>
            </div>
          </div>
        </div>
      )}
      {isFailed && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
          <div className="flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-red-900 dark:text-red-300">
                {t('live.deployment.failed')}
              </p>
              <p className="text-xs text-red-700 dark:text-red-400 mt-1">
                {t('live.deployment.failedDescription')}
              </p>
            </div>
          </div>
        </div>
      )}
      {isSuspendedNoCredits && (
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
          <p className="text-sm font-medium text-amber-900 dark:text-amber-300">
            {t('live.suspended.title')}
          </p>
          <p className="text-xs text-amber-700 dark:text-amber-400 mt-0.5">
            {t('live.suspended.description', { cost: project.live_hosting_cost })}
          </p>
        </div>
      )}

      {/* Two-column layout (desktop) / stacked (mobile) */}
      {isMobile ? (
        <div className="space-y-3">
          {urlInfo}
          {browserPreview}
          {adminCredentials}
          {deployHistory}
        </div>
      ) : (
        <div className="flex gap-4">
          {/* Left — browser chrome iframe preview */}
          <div className="flex-1 min-w-0 max-w-[50%]">
            {browserPreview}
          </div>
          {/* Right — info column */}
          <div className="flex-1 min-w-0 space-y-3">
            {urlInfo}
            {adminCredentials}
            {deployHistory}
          </div>
        </div>
      )}
    </div>
  )
}

function DeployHistory({
  project,
  processing,
  onRedeploy,
}: {
  project: Project
  processing: boolean
  onRedeploy: () => void
}) {
  const { t } = useTranslation('conversations')
  const entries = project.history_entries_recent || []

  if (entries.length === 0) {
    return (
      <div className="text-center py-4 text-sm text-muted-foreground">
        {t('live.sre.overview.noHistory')}
      </div>
    )
  }

  const deployedId = project.deployed_history_entry_id
  const currentId = project.current_history_entry_id
  const hasNewerVersion = currentId !== null && deployedId !== null && currentId !== deployedId

  return (
    <div className="space-y-2">
      <h4 className="text-sm font-medium">{t('live.sre.overview.deployHistory')}</h4>
      <div className="space-y-1">
        {entries.map((entry) => {
          const isDeployed = entry.id === deployedId
          const isFailed = entry.status === 'orphaned'

          return (
            <div
              key={entry.id}
              className={`flex items-center gap-2 rounded-md border px-3 py-2 text-xs ${
                isDeployed
                  ? 'border-green-300 dark:border-green-700 bg-green-50 dark:bg-green-900/20'
                  : isFailed
                    ? 'border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/10'
                    : 'border-border'
              }`}
            >
              {isDeployed ? (
                <Star className="h-3.5 w-3.5 text-green-600 dark:text-green-400 flex-shrink-0" />
              ) : isFailed ? (
                <X className="h-3.5 w-3.5 text-red-500 flex-shrink-0" />
              ) : (
                <div className="h-3.5 w-3.5" />
              )}
              <span className="flex-1 truncate">{entry.title}</span>
              {isDeployed && (
                <Badge variant="default" className="bg-green-600 text-[10px] px-1.5 py-0">
                  {t('live.sre.overview.liveVersion')}
                </Badge>
              )}
            </div>
          )
        })}
      </div>

      {hasNewerVersion && (
        <Button
          size="sm"
          onClick={onRedeploy}
          disabled={processing || project.production_status === 'deploying'}
          className="w-full"
        >
          {t('live.sre.overview.deployLatest')}
        </Button>
      )}
    </div>
  )
}
